import type Player from 'video.js/dist/types/player';

export interface VideoAdapter {
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlaybackRate: () => number;
  on: (event: string, handler: () => void) => void;
  off: (event: string, handler: () => void) => void;
  dispose?: () => void;
}

// Video.js adapter
export class VideoJsAdapter implements VideoAdapter {
  constructor(private readonly player: Player | null) {}

  private isPlayerValid = (): boolean => {
    try {
      return !!this.player && !this.player.isDisposed?.();
    } catch {
      return false;
    }
  };

  getCurrentTime = () => {
    try {
      return this.isPlayerValid() ? (this.player!.currentTime() ?? 0) : 0;
    } catch {
      return 0;
    }
  };

  getDuration = () => {
    try {
      return this.isPlayerValid() ? (this.player!.duration() ?? 0) : 0;
    } catch {
      return 0;
    }
  };

  getPlaybackRate = () => {
    try {
      return this.isPlayerValid() ? (this.player!.playbackRate() ?? 1) : 1;
    } catch {
      return 1;
    }
  };

  on = (event: string, handler: () => void) => {
    try {
      if (this.isPlayerValid()) {
        this.player!.on(event, handler);
      }
    } catch {
      // Silently ignore if player is unavailable
    }
  };

  off = (event: string, handler: () => void) => {
    try {
      if (this.isPlayerValid()) {
        this.player!.off(event, handler);
      }
    } catch {
      // Silently ignore if player is unavailable
    }
  };

  dispose = () => {
    // Video.js handles its own disposal
  };
}

// Native HTML5 video adapter
export class NativeVideoAdapter implements VideoAdapter {
  constructor(private readonly video: HTMLVideoElement) {}

  getCurrentTime = () => this.video.currentTime ?? 0;
  getDuration = () => this.video.duration ?? 0;
  getPlaybackRate = () => this.video.playbackRate ?? 1;

  on = (event: string, handler: () => void) => {
    this.video.addEventListener(event, handler);
  };

  off = (event: string, handler: () => void) => {
    this.video.removeEventListener(event, handler);
  };

  dispose = () => {
    // Cleanup if needed
  };
}
